import React from 'react';
import {
  Alert, Box, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import { PAGE_SIZES, WATCH_COLORS, statusColor } from './constants';
import { daysLabel, fmtDateTime, fmtMoney, fmtNumber } from './format';
import { DocumentRow, Pagination, SortKey, SortState, WatchInfo } from './types';

interface Props {
  rows: DocumentRow[];
  pagination: Pagination;
  loading: boolean;
  error: string | null;
  sort: SortState;
  showAmount: boolean;
  hasFilters: boolean;
  onSort: (key: SortKey) => void;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onOpen: (row: DocumentRow) => void;
  onResetFilters: () => void;
}

const BORDER = '#e2e8f0';

export function WatchBadge({ watch }: { watch: WatchInfo | null }) {
  if (!watch || watch.level === 'ok') return null;
  const color = WATCH_COLORS[watch.level];
  const hide = watch.action === 'hide';
  const text = watch.daysLeft > 0 ? `${hide ? 'скрытие' : 'отмена'} через ${daysLabel(watch.daysLeft)}` : 'срок истёк';
  const left = Math.max(0, watch.cancelAfter - watch.idleDays);
  return (
    <Tooltip title={`Без изменений ${daysLabel(watch.idleDays)}. ${hide ? 'Автоскрытие' : 'Автоотмена'} ${left > 0 ? `через ${daysLabel(left)}` : 'при ближайшей очистке'}`}>
      <Chip
        size="small" icon={<HourglassBottomRoundedIcon sx={{ fontSize: 14, color: `${color} !important` }} />} label={text}
        sx={{ height: 22, fontSize: 11, fontWeight: 600, color, bgcolor: `${color}14`, border: `1px solid ${color}40` }}
      />
    </Tooltip>
  );
}

export function StatusChip({ status, name }: { status: number; name: string }) {
  const color = statusColor(status);
  return (
    <Chip
      size="small" label={name}
      sx={{ height: 22, fontSize: 12, fontWeight: 600, color, bgcolor: `${color}14`, border: `1px solid ${color}33` }}
    />
  );
}

/** Обязательная маркировка возврата (null - у перемещений признака нет, ничего не выводим); те же подписи, что у аптек */
export function MarkingChip({ marked }: { marked: boolean | null }) {
  if (marked === null || marked === undefined) return null;
  return (
    <Tooltip title={marked ? 'Возврат маркированного товара: нужны коды маркировки (КМ)' : 'Возврат без обязательной маркировки'}>
      <Chip
        size="small" label={marked ? 'КМ' : 'Без КМ'}
        color={marked ? 'primary' : 'default'} variant={marked ? 'filled' : 'outlined'}
        sx={{ height: 20, fontSize: 11, fontWeight: 700, ...(marked ? {} : { borderColor: BORDER, color: '#64748b' }) }}
      />
    </Tooltip>
  );
}

const COLUMNS: Array<{ key: SortKey | null; label: string; align?: 'left' | 'right'; width?: number | string }> = [
  { key: 'id', label: '№', width: 84 },
  { key: 'type', label: 'Тип', width: 160 },
  { key: 'from', label: 'Откуда → Куда' },
  { key: 'status', label: 'Статус', width: 190 },
  { key: null, label: 'Позиции', align: 'right', width: 110 },
  { key: 'created', label: 'Создан', width: 120 },
  { key: 'updated', label: 'Обновлён', width: 120 },
  { key: 'author', label: 'Автор', width: 150 }
];

const DocumentsTable: React.FC<Props> = ({
  rows, pagination, loading, error, sort, showAmount, hasFilters,
  onSort, onPage, onLimit, onOpen, onResetFilters
}) => {
  const columns = showAmount
    ? [...COLUMNS.slice(0, 5), { key: null as null, label: 'Сумма', align: 'right' as const, width: 120 }, ...COLUMNS.slice(5)]
    : COLUMNS;

  return (
    <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      {error && <Alert severity="error" sx={{ m: 1.5 }}>{error}</Alert>}

      <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)', minHeight: 240 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 980, opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.label} align={col.align || 'left'} sortDirection={col.key && sort.sortBy === col.key ? sort.sortOrder : false}
                  sx={{ width: col.width, fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}
                >
                  {col.key ? (
                    <TableSortLabel
                      active={sort.sortBy === col.key} direction={sort.sortBy === col.key ? sort.sortOrder : 'asc'}
                      onClick={() => onSort(col.key as SortKey)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow
                key={row.id} hover onClick={() => onOpen(row)}
                sx={{ cursor: 'pointer', '& td': { borderBottom: `1px solid #f1f5f9`, py: 0.9 }, bgcolor: row.isHidden ? '#fafafa' : undefined }}
              >
                <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {row.id}
                    {row.isHidden && (
                      <Tooltip title="Документ скрыт у аптек"><VisibilityOffOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8' }} /></Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: '#475569' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {row.typeName}
                    <MarkingChip marked={row.marked} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.sender || '-'}</Typography>
                    <ArrowForwardRoundedIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.recipient || '-'}</Typography>
                  </Box>
                  {row.route === 'via' && (
                    <Typography variant="caption" color="text.secondary">через {row.via || 'РЦ'}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <StatusChip status={row.status} name={row.statusName} />
                    <WatchBadge watch={row.watch} />
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>
                  <span>{fmtNumber(row.itemsCount)} поз.</span>
                  <Typography component="div" variant="caption" color="text.secondary">{fmtNumber(row.totalQty)} шт.</Typography>
                </TableCell>
                {showAmount && (
                  <TableCell align="right" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {row.amount === null || row.amount === undefined ? '-' : fmtMoney(row.amount)}
                  </TableCell>
                )}
                <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDateTime(row.createdAt)}</TableCell>
                <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDateTime(row.updatedAt)}</TableCell>
                <TableCell sx={{ fontSize: 13, color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.author || '-'}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading && !error && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">{hasFilters ? 'По заданным фильтрам документов не найдено' : 'Документов нет'}</Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
        rowsPerPageOptions={PAGE_SIZES}
        onPageChange={(_, p) => onPage(p + 1)}
        onRowsPerPageChange={e => onLimit(Number(e.target.value))}
        labelRowsPerPage="На странице:"
        labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
        sx={{ borderTop: `1px solid ${BORDER}` }}
      />
    </Paper>
  );
};

export default DocumentsTable;
